import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  InstagramLogo, LinkedinLogo, FacebookLogo, PaperPlaneTilt, CalendarBlank,
  CheckCircle, WarningCircle, ArrowsClockwise, Sparkle, Info,
  ImageSquare, VideoCamera, Newspaper, CaretDown, Clock, X, Check
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_META = {
  instagram: { icon: InstagramLogo, color: '#E4405F', name: 'Instagram', maxCaption: 2200 },
  facebook: { icon: FacebookLogo, color: '#1877F2', name: 'Facebook', maxCaption: 63206 },
  linkedin: { icon: LinkedinLogo, color: '#0A66C2', name: 'LinkedIn', maxCaption: 3000 },
};

export default function PublishComposerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [caption, setCaption] = useState('');
  const [cta, setCta] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [contentType, setContentType] = useState('image');
  const [mediaAssets, setMediaAssets] = useState([]);
  const [perPlatformOverrides, setPerPlatformOverrides] = useState({});
  const [activeTab, setActiveTab] = useState('master');
  const [publishing, setPublishing] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [result, setResult] = useState(null);
  const [validation, setValidation] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    fetchAccounts();
    // Prefill from URL params
    const prefillCaption = searchParams.get('caption');
    const prefillType = searchParams.get('type');
    if (prefillCaption) setCaption(decodeURIComponent(prefillCaption));
    if (prefillType) setContentType(prefillType);
  }, []);

  const fetchAccounts = async () => {
    try {
      const { data } = await axios.get(`${API}/api/social/accounts`);
      setAccounts(data.accounts || []);
    } catch {}
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const getOverride = (platform, field) => {
    return perPlatformOverrides[platform]?.[field] ?? '';
  };

  const setOverride = (platform, field, value) => {
    setPerPlatformOverrides(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  // Auto-save draft
  const saveDraft = useCallback(async (silent = true) => {
    try {
      const { data } = await axios.post(`${API}/api/publishing/drafts`, {
        draft_id: draftId,
        master_caption: caption,
        master_cta: cta,
        master_hashtags: hashtags.split(/[,\s]+/).filter(Boolean),
        selected_platforms: selectedPlatforms,
        content_type: contentType,
        media_assets: mediaAssets,
        per_platform_overrides: perPlatformOverrides,
        source_module: 'composer',
      });
      if (!draftId && data.draft?.draft_id) setDraftId(data.draft.draft_id);
      setLastSaved(new Date().toLocaleTimeString());
    } catch {}
  }, [caption, cta, hashtags, selectedPlatforms, contentType, mediaAssets, perPlatformOverrides, draftId]);

  useEffect(() => {
    if (!caption && !cta) return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    const timer = setTimeout(() => saveDraft(true), 3000);
    setAutoSaveTimer(timer);
    return () => clearTimeout(timer);
  }, [caption, cta, hashtags, selectedPlatforms, contentType, perPlatformOverrides]);

  const validate = async () => {
    try {
      const { data } = await axios.post(`${API}/api/publishing/validate`, {
        master_caption: caption,
        master_hashtags: hashtags.split(/[,\s]+/).filter(Boolean),
        selected_platforms: selectedPlatforms,
        content_type: contentType,
        media_assets: mediaAssets,
      });
      setValidation(data);
      return data.valid;
    } catch {
      return false;
    }
  };

  const handlePublish = async () => {
    const isValid = await validate();
    if (!isValid) return;

    setPublishing(true);
    setResult(null);
    try {
      await saveDraft(true);
      const { data } = await axios.post(`${API}/api/publishing/publish`, {
        draft_id: draftId || undefined,
        master_caption: caption,
        master_cta: cta,
        master_hashtags: hashtags.split(/[,\s]+/).filter(Boolean),
        selected_platforms: selectedPlatforms,
        content_type: contentType,
        media_assets: mediaAssets,
        per_platform_overrides: perPlatformOverrides,
      });
      setResult(data);
      if (data.draft_id) setDraftId(data.draft_id);
    } catch (err) {
      setResult({ status: 'error', error: err.response?.data?.detail || 'Publishing failed' });
    }
    setPublishing(false);
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) return;
    const isValid = await validate();
    if (!isValid) return;

    setScheduling(true);
    try {
      await saveDraft(true);
      const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      await axios.post(`${API}/api/publishing/schedule`, {
        draft_id: draftId,
        scheduled_for: scheduledFor,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setResult({ status: 'scheduled', message: `Scheduled for ${scheduleDate} at ${scheduleTime}` });
      setShowSchedule(false);
    } catch (err) {
      setResult({ status: 'error', error: err.response?.data?.detail || 'Scheduling failed' });
    }
    setScheduling(false);
  };

  const connectedPlatforms = accounts.filter(a => a.is_active).map(a => a.platform);
  const currentCaption = activeTab === 'master' ? caption : (getOverride(activeTab, 'caption') || caption);
  const currentCaptionLength = currentCaption.length;
  const maxLen = activeTab !== 'master' ? (PLATFORM_META[activeTab]?.maxCaption || 99999) : 99999;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="publish-composer-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Publish
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.4)' }}>
              Customize and publish to multiple platforms at once.
            </p>
          </div>
          {lastSaved && (
            <span className="text-[10px] px-2 py-1 rounded-md" style={{ background: 'rgba(200,240,96,0.04)', color: 'rgba(200,240,96,0.4)' }}>
              Draft saved {lastSaved}
            </span>
          )}
        </div>

        {/* Demo banner */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg" data-testid="demo-banner-composer"
          style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.08)' }}>
          <Info size={14} style={{ color: '#c8f060' }} />
          <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.35)' }}>
            <span className="font-semibold" style={{ color: '#c8f060' }}>Demo Mode</span> — Live posting not yet enabled. Posts simulate the full publishing flow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left: Compose */}
          <div className="lg:col-span-7 space-y-4">
            {/* Platform selector */}
            <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: 'rgba(245,240,232,0.3)' }}>Publish To</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(PLATFORM_META).map(([id, meta]) => {
                  const Icon = meta.icon;
                  const isConnected = connectedPlatforms.includes(id);
                  const isSelected = selectedPlatforms.includes(id);
                  return (
                    <button
                      key={id}
                      onClick={() => isConnected && togglePlatform(id)}
                      disabled={!isConnected}
                      data-testid={`platform-toggle-${id}`}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all"
                      style={{
                        background: isSelected ? `${meta.color}15` : 'rgba(255,255,255,0.02)',
                        border: isSelected ? `1px solid ${meta.color}40` : '1px solid rgba(255,255,255,0.06)',
                        opacity: isConnected ? 1 : 0.3,
                        cursor: isConnected ? 'pointer' : 'default',
                      }}
                    >
                      <Icon size={18} weight="fill" style={{ color: isSelected ? meta.color : 'rgba(245,240,232,0.3)' }} />
                      <span className="text-xs font-medium" style={{ color: isSelected ? '#f5f0e8' : 'rgba(245,240,232,0.3)' }}>
                        {meta.name}
                      </span>
                      {isSelected && <Check size={12} weight="bold" style={{ color: meta.color }} />}
                    </button>
                  );
                })}
              </div>
              {connectedPlatforms.length === 0 && (
                <button
                  onClick={() => navigate('/dashboard/social-accounts')}
                  className="mt-3 text-xs font-medium px-3 py-1.5 rounded-lg"
                  style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.12)' }}
                  data-testid="connect-accounts-cta"
                >
                  Connect accounts to start publishing
                </button>
              )}
            </div>

            {/* Platform tabs */}
            {selectedPlatforms.length > 0 && (
              <div className="flex items-center gap-1 overflow-x-auto">
                <button onClick={() => setActiveTab('master')} data-testid="tab-master"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                  style={{
                    background: activeTab === 'master' ? 'rgba(200,240,96,0.08)' : 'transparent',
                    color: activeTab === 'master' ? '#c8f060' : 'rgba(245,240,232,0.3)',
                    border: activeTab === 'master' ? '1px solid rgba(200,240,96,0.15)' : '1px solid transparent',
                  }}>
                  All Platforms
                </button>
                {selectedPlatforms.map(p => {
                  const meta = PLATFORM_META[p];
                  const Icon = meta?.icon;
                  return (
                    <button key={p} onClick={() => setActiveTab(p)} data-testid={`tab-${p}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all"
                      style={{
                        background: activeTab === p ? `${meta.color}12` : 'transparent',
                        color: activeTab === p ? meta.color : 'rgba(245,240,232,0.3)',
                        border: activeTab === p ? `1px solid ${meta.color}30` : '1px solid transparent',
                      }}>
                      {Icon && <Icon size={13} weight="fill" />}
                      {meta.name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Caption editor */}
            <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>
                  {activeTab === 'master' ? 'Caption' : `${PLATFORM_META[activeTab]?.name} Caption`}
                </label>
                <span className="text-[10px] font-mono" style={{ color: currentCaptionLength > maxLen ? '#ef4444' : 'rgba(245,240,232,0.2)' }}>
                  {currentCaptionLength}{maxLen < 99999 ? `/${maxLen}` : ''}
                </span>
              </div>
              <textarea
                value={activeTab === 'master' ? caption : (getOverride(activeTab, 'caption') || '')}
                onChange={e => activeTab === 'master' ? setCaption(e.target.value) : setOverride(activeTab, 'caption', e.target.value)}
                placeholder={activeTab === 'master' ? 'Write your caption...' : `Override for ${PLATFORM_META[activeTab]?.name} (leave empty to use master caption)`}
                data-testid="caption-input"
                rows={5}
                className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8', outline: 'none', lineHeight: 1.6 }}
              />

              {activeTab === 'master' && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Hashtags</label>
                    <input
                      value={hashtags}
                      onChange={e => setHashtags(e.target.value)}
                      placeholder="marketing, startup, growth"
                      data-testid="hashtags-input"
                      className="w-full px-3 py-2 rounded-lg text-xs"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8', outline: 'none' }}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Call to Action</label>
                    <input
                      value={cta}
                      onChange={e => setCta(e.target.value)}
                      placeholder="Learn more at..."
                      data-testid="cta-input"
                      className="w-full px-3 py-2 rounded-lg text-xs"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8', outline: 'none' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Content type */}
            <div className="p-4 rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>Content Type</p>
              <div className="flex gap-2">
                {[
                  { id: 'image', icon: ImageSquare, label: 'Image' },
                  { id: 'video', icon: VideoCamera, label: 'Video' },
                  { id: 'carousel', icon: Newspaper, label: 'Carousel' },
                ].map(t => (
                  <button key={t.id} onClick={() => setContentType(t.id)} data-testid={`content-type-${t.id}`}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all"
                    style={{
                      background: contentType === t.id ? 'rgba(200,240,96,0.06)' : 'rgba(255,255,255,0.02)',
                      color: contentType === t.id ? '#c8f060' : 'rgba(245,240,232,0.3)',
                      border: contentType === t.id ? '1px solid rgba(200,240,96,0.15)' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <t.icon size={14} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Validation errors */}
            <AnimatePresence>
              {validation && !validation.valid && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="space-y-1.5">
                    {validation.errors.map((err, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>
                        <WarningCircle size={14} className="text-red-400 flex-shrink-0" />
                        <p className="text-xs text-red-400">{err.message}</p>
                      </div>
                    ))}
                    {validation.warnings?.map((warn, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)' }}>
                        <Info size={14} className="text-amber-400 flex-shrink-0" />
                        <p className="text-xs text-amber-400">{warn.message}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handlePublish}
                disabled={publishing || selectedPlatforms.length === 0}
                data-testid="publish-now-btn"
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: '#c8f060', color: '#080808', opacity: (publishing || selectedPlatforms.length === 0) ? 0.4 : 1 }}
              >
                {publishing ? <ArrowsClockwise size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
                {publishing ? 'Publishing...' : 'Publish Now'}
              </button>
              <button
                onClick={() => setShowSchedule(true)}
                disabled={selectedPlatforms.length === 0}
                data-testid="schedule-btn"
                className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)', opacity: selectedPlatforms.length === 0 ? 0.4 : 1 }}
              >
                <CalendarBlank size={16} />
                Schedule
              </button>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-5">
            <div className="sticky top-20 space-y-4">
              <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.3)' }}>Preview</p>

              {selectedPlatforms.length === 0 ? (
                <div className="py-16 text-center rounded-xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <PaperPlaneTilt size={32} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.08)' }} />
                  <p className="text-sm" style={{ color: 'rgba(245,240,232,0.2)' }}>Select platforms to preview</p>
                </div>
              ) : (
                selectedPlatforms.map(p => {
                  const meta = PLATFORM_META[p];
                  const Icon = meta?.icon;
                  const override = perPlatformOverrides[p]?.caption;
                  const displayCaption = override || caption;
                  const displayHashtags = hashtags.split(/[,\s]+/).filter(Boolean);

                  return (
                    <div key={p} className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`preview-${p}`}>
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                        {Icon && <Icon size={14} weight="fill" style={{ color: meta.color }} />}
                        <span className="text-[11px] font-medium" style={{ color: '#f5f0e8' }}>{meta.name} Preview</span>
                        {override && <span className="text-[9px] px-1.5 py-0.5 rounded-md ml-auto" style={{ background: `${meta.color}15`, color: meta.color }}>Customized</span>}
                      </div>
                      <div className="p-4">
                        {/* Mock post preview */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-full" style={{ background: `${meta.color}20` }} />
                          <div>
                            <p className="text-[11px] font-semibold" style={{ color: '#f5f0e8' }}>
                              {accounts.find(a => a.platform === p)?.display_name || 'Your Account'}
                            </p>
                            <p className="text-[9px]" style={{ color: 'rgba(245,240,232,0.2)' }}>Just now</p>
                          </div>
                        </div>
                        {contentType === 'image' && (
                          <div className="w-full aspect-square rounded-lg mb-3 flex items-center justify-center" style={{ background: '#0a0a0a' }}>
                            <ImageSquare size={32} style={{ color: 'rgba(245,240,232,0.06)' }} />
                          </div>
                        )}
                        <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(245,240,232,0.6)' }}>
                          {displayCaption || 'Your caption will appear here...'}
                          {cta && <span className="block mt-1" style={{ color: '#f5f0e8' }}>{cta}</span>}
                          {p === 'instagram' && displayHashtags.length > 0 && (
                            <span className="block mt-2" style={{ color: 'rgba(245,240,232,0.3)' }}>
                              {displayHashtags.map(t => `#${t.replace('#', '')}`).join(' ')}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Publish result */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg mx-4" data-testid="publish-result">
              <div className="rounded-xl p-4 shadow-2xl" style={{
                background: result.status === 'published' || result.status === 'scheduled' ? '#141414' : '#1a0a0a',
                border: result.status === 'published' || result.status === 'scheduled' ? '1px solid rgba(200,240,96,0.2)' : '1px solid rgba(239,68,68,0.2)',
              }}>
                <div className="flex items-start gap-3">
                  {result.status === 'published' || result.status === 'scheduled' ? (
                    <CheckCircle size={20} weight="fill" className="text-green-400 flex-shrink-0 mt-0.5" />
                  ) : result.status === 'partially_published' ? (
                    <WarningCircle size={20} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <WarningCircle size={20} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{ color: '#f5f0e8' }}>
                      {result.status === 'published' && `Published to ${result.summary?.succeeded} platform${result.summary?.succeeded > 1 ? 's' : ''}`}
                      {result.status === 'partially_published' && `Published to ${result.summary?.succeeded}/${result.summary?.total} platforms`}
                      {result.status === 'scheduled' && result.message}
                      {result.status === 'failed' && 'Publishing failed'}
                      {result.status === 'error' && (result.error || 'Something went wrong')}
                    </p>
                    {result.results && (
                      <div className="mt-2 space-y-1">
                        {result.results.map((r, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {r.status === 'published' ? (
                              <CheckCircle size={12} weight="fill" className="text-green-400" />
                            ) : (
                              <WarningCircle size={12} weight="fill" className="text-red-400" />
                            )}
                            <span className="text-[11px]" style={{ color: 'rgba(245,240,232,0.5)' }}>
                              {r.platform} — {r.status === 'published' ? 'Success' : (r.error_message || 'Failed')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setResult(null)} className="text-zinc-500 hover:text-white"><X size={14} /></button>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => navigate('/dashboard/post-history')} className="text-[10px] font-medium px-2.5 py-1 rounded-md"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.4)' }}>View History</button>
                  <button onClick={() => { setResult(null); setCaption(''); setCta(''); setHashtags(''); setSelectedPlatforms([]); setDraftId(null); }}
                    className="text-[10px] font-medium px-2.5 py-1 rounded-md"
                    style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060' }}>New Post</button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Schedule modal */}
        <AnimatePresence>
          {showSchedule && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[140] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
              onClick={() => setShowSchedule(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="w-full max-w-sm mx-4 rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
                onClick={e => e.stopPropagation()} data-testid="schedule-modal">
                <h3 className="text-sm font-semibold mb-1" style={{ color: '#f5f0e8' }}>Schedule Post</h3>
                <p className="text-[11px] mb-4" style={{ color: 'rgba(245,240,232,0.3)' }}>
                  Choose date and time ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Date</label>
                    <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} data-testid="schedule-date"
                      className="w-full px-3 py-2 rounded-lg text-xs"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8', outline: 'none', colorScheme: 'dark' }} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider mb-1 block" style={{ color: 'rgba(245,240,232,0.3)' }}>Time</label>
                    <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} data-testid="schedule-time"
                      className="w-full px-3 py-2 rounded-lg text-xs"
                      style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8', outline: 'none', colorScheme: 'dark' }} />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setShowSchedule(false)} className="flex-1 py-2.5 rounded-xl text-xs font-medium"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(255,255,255,0.06)' }}>Cancel</button>
                  <button onClick={handleSchedule} disabled={scheduling || !scheduleDate || !scheduleTime}
                    data-testid="confirm-schedule-btn"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold"
                    style={{ background: '#c8f060', color: '#080808', opacity: (scheduling || !scheduleDate || !scheduleTime) ? 0.4 : 1 }}>
                    {scheduling ? <ArrowsClockwise size={12} className="animate-spin" /> : <CalendarBlank size={12} weight="fill" />}
                    {scheduling ? 'Scheduling...' : 'Schedule'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
